/**
 * Cachés a nivel módulo atadas a la SESIÓN de usuario (stale-while-revalidate).
 * Extraído de speak — es el mecanismo genérico que necesita cualquier app de la
 * familia con cachés module-level.
 *
 * Problema que resuelve: hooks de datos que re-fetchean en cada mount hacen que
 * cada visita a una pantalla arranque vacía aunque los datos se hayan leído
 * segundos antes. Con un caché de módulo, la revisita monta con la última
 * lectura al instante y el fetch de fondo revalida.
 *
 * Contrato:
 *   - `createSessionCache<T>()` se llama UNA vez por módulo (top-level). Cada
 *     llamada registra el caché en un set global para poder resetearlo.
 *   - `clearSessionCaches()` vacía TODOS los cachés registrados. Lo dispara el
 *     AuthProvider cuando cambia la identidad (otp/signout): el logout NO
 *     recarga la página, así que sin esto un segundo usuario en la misma
 *     sesión SPA vería datos del anterior.
 */

interface SessionCache<T> {
  /** Última lectura conocida, o `null` si nunca se resolvió (o se limpió). */
  get: () => T | null;
  set: (value: T | null) => void;
}

const resets = new Set<() => void>();

/** Crea un caché de sesión. Llamar sólo a nivel módulo (se registra global). */
export function createSessionCache<T>(): SessionCache<T> {
  let value: T | null = null;
  resets.add(() => {
    value = null;
  });
  return {
    get: () => value,
    set: (next) => {
      value = next;
    },
  };
}

/** Vacía todos los cachés de sesión. Llamar cuando cambia el usuario. */
export function clearSessionCaches(): void {
  for (const reset of resets) reset();
}
